import AuthAPI from '../api/auth';
import BaseActionCableConnector from '../../shared/helpers/BaseActionCableConnector';
import DashboardAudioNotificationHelper from './AudioAlerts/DashboardAudioNotificationHelper';
import browserNotificationHelper from './BrowserNotificationHelper';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import { forceCleanup } from 'dashboard/composables/useWacallsCall';
import { useImpersonation } from 'dashboard/composables/useImpersonation';

const { isImpersonating } = useImpersonation();

class ActionCableConnector extends BaseActionCableConnector {
  constructor(app, pubsubToken) {
    const { websocketURL = '' } = window.chatwootConfig || {};
    super(app, pubsubToken, websocketURL);
    this.CancelTyping = [];
    this.events = {
      'message.created': this.onMessageCreated,
      'message.updated': this.onMessageUpdated,
      'conversation.created': this.onConversationCreated,
      'conversation.status_changed': this.onStatusChange,
      'user:logout': this.onLogout,
      'page:reload': this.onReload,
      'assignee.changed': this.onAssigneeChanged,
      'conversation.typing_on': this.onTypingOn,
      'conversation.typing_off': this.onTypingOff,
      'conversation.contact_changed': this.onConversationContactChange,
      'presence.update': this.onPresenceUpdate,
      'contact.deleted': this.onContactDelete,
      'contact.updated': this.onContactUpdate,
      'conversation.mentioned': this.onConversationMentioned,
      'notification.created': this.onNotificationCreated,
      'notification.deleted': this.onNotificationDeleted,
      'notification.updated': this.onNotificationUpdated,
      'conversation.read': this.onConversationRead,
      'conversation.updated': this.onConversationUpdated,
      'account.cache_invalidated': this.onCacheInvalidate,
      'whatsapp.qrcode_updated': this.onWhatsappQrCodeUpdated,
      'config_assistant.updated': this.onConfigAssistantUpdated,
      'whatsapp.connection_update': this.onWhatsappConnectionUpdate,
      // Chat Room events
      'chat_room_message.created': this.onChatRoomMessageCreated,
      'chat_room_message.updated': this.onChatRoomMessageUpdated,
      'chat_room.deleted': this.onChatRoomDeleted,
      'chat_room.member_added': this.onChatRoomMemberAdded,
      'chat_room.member_removed': this.onChatRoomMemberRemoved,
      // WhatsApp calling events
      'call.answer': this.onCallAnswer,
      'call.status': this.onCallStatus,
      'call.terminated': this.onCallTerminated,
      'call.incoming': this.onCallIncoming,
      'call.accepted': this.onCallAccepted,
      // WaCalls (unofficial) calling events
      'wacalls_call.incoming': this.onWacallsCallIncoming,
      'wacalls_call.answered': this.onWacallsCallAnswered,
      'wacalls_call.terminated': this.onWacallsCallTerminated,
    };
  }

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_RECONNECT);
  };

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_DISCONNECT);
  };

  isAValidEvent = data => {
    return this.app.$store.getters.getCurrentAccountId === data.account_id;
  };

  onMessageUpdated = data => {
    this.app.$store.dispatch('updateMessage', data);
  };

  onPresenceUpdate = data => {
    if (isImpersonating.value) return;
    this.app.$store.dispatch('contacts/updatePresence', data.contacts);
    this.app.$store.dispatch('agents/updatePresence', data.users);
    this.app.$store.dispatch('setCurrentUserAvailability', data.users);
  };

  onConversationContactChange = payload => {
    const { meta = {}, id: conversationId } = payload;
    const { sender } = meta || {};
    if (conversationId) {
      this.app.$store.dispatch('updateConversationContact', {
        conversationId,
        ...sender,
      });
    }
  };

  onAssigneeChanged = payload => {
    const { id } = payload;
    if (id) {
      this.app.$store.dispatch('updateConversation', payload);
    }
    this.fetchConversationStats();
  };

  onConversationCreated = data => {
    this.app.$store.dispatch('addConversation', data);
    this.fetchConversationStats();
  };

  onConversationRead = data => {
    this.app.$store.dispatch('updateConversation', data);
  };

  // eslint-disable-next-line class-methods-use-this
  onLogout = () => AuthAPI.logout();

  onMessageCreated = data => {
    const {
      conversation: { last_activity_at: lastActivityAt },
      conversation_id: conversationId,
    } = data;
    DashboardAudioNotificationHelper.onNewMessage(data);
    this.app.$store.dispatch('addMessage', data);
    // Mensagem que nasceu de um agendamento: some da faixa de agendadas.
    const scheduledId = data.additional_attributes?.scheduled_message_id;
    if (scheduledId) {
      this.app.$store.dispatch('scheduledMessages/removeLocal', {
        conversationId,
        id: scheduledId,
      });
    }
    // Nota de sequência interrompida por resposta do cliente: some a faixa fantasma.
    const stoppedSequenceId =
      data.additional_attributes?.scheduled_sequence_stopped;
    if (stoppedSequenceId) {
      this.app.$store.dispatch('scheduledMessages/removeSequenceLocal', {
        conversationId,
        sequenceId: stoppedSequenceId,
      });
    }
    this.app.$store.dispatch('updateConversationLastActivity', {
      lastActivityAt,
      conversationId,
    });
  };

  // eslint-disable-next-line class-methods-use-this
  onReload = () => window.location.reload();

  onStatusChange = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
  };

  onConversationUpdated = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
  };

  onTypingOn = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/create', {
      conversationId,
      user,
    });
    this.initTimer({ conversation, user });
  };

  onTypingOff = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/destroy', {
      conversationId,
      user,
    });
  };

  onConversationMentioned = data => {
    this.app.$store.dispatch('addMentions', data);
  };

  clearTimer = conversationId => {
    const timerEvent = this.CancelTyping[conversationId];

    if (timerEvent) {
      clearTimeout(timerEvent);
      this.CancelTyping[conversationId] = null;
    }
  };

  initTimer = ({ conversation, user }) => {
    const conversationId = conversation.id;
    // Turn off typing automatically after 30 seconds
    this.CancelTyping[conversationId] = setTimeout(() => {
      this.onTypingOff({ conversation, user });
    }, 30000);
  };

  // eslint-disable-next-line class-methods-use-this
  fetchConversationStats = () => {
    emitter.emit('fetch_conversation_stats');
  };

  onContactDelete = data => {
    this.app.$store.dispatch(
      'contacts/deleteContactThroughConversations',
      data.id
    );
    this.fetchConversationStats();
  };

  onContactUpdate = data => {
    this.app.$store.dispatch('contacts/updateContact', data);
  };

  onNotificationCreated = data => {
    this.app.$store.dispatch('notifications/addNotification', data);
  };

  onNotificationDeleted = data => {
    this.app.$store.dispatch('notifications/deleteNotification', data);
  };

  onNotificationUpdated = data => {
    this.app.$store.dispatch('notifications/updateNotification', data);
  };

  // eslint-disable-next-line class-methods-use-this
  onWhatsappQrCodeUpdated = data => {
    emitter.emit(BUS_EVENTS.WHATSAPP_QRCODE_UPDATED, data);
  };

  // eslint-disable-next-line class-methods-use-this
  onConfigAssistantUpdated = data => {
    emitter.emit(BUS_EVENTS.CONFIG_ASSISTANT_UPDATED, data);
  };

  // eslint-disable-next-line class-methods-use-this
  onWhatsappConnectionUpdate = data => {
    emitter.emit(BUS_EVENTS.WHATSAPP_CONNECTION_UPDATE, data);
  };

  onCacheInvalidate = data => {
    const keys = data.cache_keys;
    this.app.$store.dispatch('labels/revalidate', { newKey: keys.label });
    this.app.$store.dispatch('inboxes/revalidate', { newKey: keys.inbox });
    this.app.$store.dispatch('teams/revalidate', { newKey: keys.team });
  };

  // Chat Room event handlers
  onChatRoomMessageCreated = data => {
    emitter.emit(BUS_EVENTS.CHAT_ROOM_MESSAGE_CREATED, data);

    // Show notifications for messages from other users
    const currentUser = this.app.$store.getters.getCurrentUser;
    if (data.sender?.id !== currentUser?.id) {
      const chatRoom = this.app.$store.getters['chatRooms/getChatRoom'](
        data.conversation_id
      );
      browserNotificationHelper.showChatRoomNotification(data, chatRoom);

      // Increment unread count if not viewing this room
      this.app.$store.commit(
        'chatRooms/INCREMENT_UNREAD_COUNT',
        data.conversation_id
      );

      // Show in-app toast notification
      const senderName = data.sender?.name || data.sender?.available_name || '';
      const roomName = data.room_name || chatRoom?.name || '';
      const preview =
        data.content?.length > 60
          ? `${data.content.substring(0, 60)}...`
          : data.content || '';
      const toastMessage = `💬 ${senderName} em ${roomName}: ${preview}`;
      emitter.emit('newToastMessage', { message: toastMessage });
    }
  };

  // eslint-disable-next-line class-methods-use-this
  onChatRoomMessageUpdated = data => {
    emitter.emit(BUS_EVENTS.CHAT_ROOM_MESSAGE_UPDATED, data);
  };

  // eslint-disable-next-line class-methods-use-this
  onChatRoomDeleted = data => {
    this.app.$store.dispatch('chatRooms/DELETE_CHAT_ROOM', data.id);
    emitter.emit(BUS_EVENTS.CHAT_ROOM_DELETED, data);
  };

  // eslint-disable-next-line class-methods-use-this
  onChatRoomMemberAdded = data => {
    emitter.emit(BUS_EVENTS.CHAT_ROOM_MEMBER_ADDED, data);
  };

  // eslint-disable-next-line class-methods-use-this
  onChatRoomMemberRemoved = data => {
    emitter.emit(BUS_EVENTS.CHAT_ROOM_MEMBER_REMOVED, data);
  };

  // WhatsApp calling event handlers
  onCallAnswer = data => {
    // C3: also push the status from the answer payload so the panel advances past "Calling…"
    this.app.$store.dispatch('whatsappCall/setStatus', data.status || 'active');
    emitter.emit('whatsapp_call:answer', {
      callId: data.call_id,
      sdp: data.sdp,
    });
  };

  onCallStatus = data => {
    this.app.$store.dispatch('whatsappCall/setStatus', data.status);
  };

  onCallTerminated = data => {
    // I3: backend enum / broadcast uses 'ended', not 'completed'
    this.app.$store.dispatch('whatsappCall/setStatus', 'ended');
    emitter.emit('whatsapp_call:terminated', { callId: data.call_id });
  };

  onCallIncoming = data => {
    this.app.$store.dispatch('whatsappCall/setIncoming', {
      recordId: data.record_id,
      conversationId: data.conversation_id,
      contactName: data.contact_name,
      phone: data.phone,
      sdpOffer: data.sdp_offer,
    });
    emitter.emit('whatsapp_call:incoming', data);
  };

  // eslint-disable-next-line class-methods-use-this
  onCallAccepted = data => {
    emitter.emit('whatsapp_call:accepted', { recordId: data.record_id });
  };

  onWacallsCallIncoming = data => {
    this.app.$store.dispatch('wacallsCall/setIncoming', {
      conversationId: data.conversation_id,
      callId: data.call_id,
      contactName: data.contact_name,
      phone: data.phone,
      sessionId: data.session_id,
    });
    emitter.emit('wacalls_call:incoming', data);
  };

  onWacallsCallAnswered = data => {
    // Outro atendente (ou o próprio celular) atendeu: some o aviso de chamada
    // recebida aqui, senão ele continua tocando para os demais.
    const incoming = this.app.$store.getters['wacallsCall/getIncoming'];
    if (incoming?.callId && incoming.callId === data.call_id) {
      this.app.$store.dispatch('wacallsCall/clearIncoming');
    }

    const activeCall = this.app.$store.getters['wacallsCall/getActiveCall'];
    if (activeCall?.callId && activeCall.callId === data.call_id) {
      this.app.$store.dispatch('wacallsCall/setStatus', 'active');
    }
  };

  onWacallsCallTerminated = data => {
    this.app.$store.dispatch('wacallsCall/clearIncoming');

    // Close an active outbound/inbound call panel when the remote side ends
    // the call (no-answer, callee hangs up) so the agent doesn't have to.
    const activeCall = this.app.$store.getters['wacallsCall/getActiveCall'];
    if (activeCall?.callId && activeCall.callId === data.call_id) {
      forceCleanup();
      this.app.$store.dispatch('wacallsCall/reset');
    }

    emitter.emit('wacalls_call:terminated', { callId: data.call_id });
  };
}

export default {
  init(store, pubsubToken) {
    return new ActionCableConnector({ $store: store }, pubsubToken);
  },
};
